import { useRef, useEffect } from 'react';
import {
  ArrowUpRight,
  MessageSquareWarning,
  Upload,
  UploadCloud,
  FileText,
  FolderOpen,
  Users,
} from 'lucide-react';
import { useSelector, useDispatch } from 'react-redux';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { usePostHog } from '@posthog/react';
import RewardCoin from '@/components/Icons/RewardCoin';
import { Button } from '../ui/button';
import LoginDialog, { LoginButton } from '@/components/Navbar/LoginDialog';
import { selectCurrentUser, setLogout } from '@/redux/auth/auth.slice';
import { useLogoutMutation } from '@/redux/auth/auth.api.slice';
import { setShowLoginDialog } from '@/redux/global/global.slice';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import filefinityMark from '@/assets/filefinity-logo.png';
import onefinityMark from '@/assets/onefinity.png';
import ProfilePicture from '../ui/profile-picture';
import VerifyAccountModal from '@/redux/auth/VerifyAccountModal';
import MobileNav from './MobileNav';
import { FeedTile } from './FeedLink';
import FeedSheet from '@/components/Feed/FeedSheet';
import FeedSearchInput from '@/pages/Landing/components/FeedSearchInput';
import { FeedFilterChips, FeedFilterControls } from '@/pages/Landing/FilterBar';
import ProfileStatusTabs from '@/pages/Profile/FilterBar';
import ProfileSearchRow from '@/pages/Profile/ProfileSearchRow';
import RewardsProgramTermsModal from '@/components/RewardsProgramTermsModal';
import ForgetPasswordDialog from '@/redux/auth/ForgetPassword/ForgetPasswordModal';
import useHideOnScroll from '@/customHooks/useHideOnScroll';

const TILE =
  'flex h-11 items-center gap-2 whitespace-nowrap rounded-xl border border-border bg-card px-3 text-[13px] font-medium text-heading transition-colors hover:border-green-600 hover:bg-green-50 hover:text-green-700';

const GUTTER = 'px-4 md:px-6';

/** Surfaces with their own row in the chrome; the brand row folds away for them. */
type Surface = 'feed' | 'profile';

const FOLD_GATE: Record<Surface, string> = {
  feed: 'top-results',
  profile: 'profile-projects',
};

function Navbar({ surface }: { surface?: Surface }) {
  const { isAuthenticated, user, isVerified } = useSelector(selectCurrentUser);
  const [logout] = useLogoutMutation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const posthog = usePostHog();
  const navRef = useRef<HTMLDivElement>(null);

  // Reading down a list, the brand row folds away and the search row stays.
  // Off feed/profile the brand row never folds, so the search row below it
  // does not start a fold there.
  const [searchParams] = useSearchParams();
  const gated = useHideOnScroll(surface && FOLD_GATE[surface]);
  const showBrandRow = !surface || gated;

  // Set during render, so it is already current when ResizeObserver fires.
  const brandRowVisible = useRef(showBrandRow);
  brandRowVisible.current = showBrandRow;

  // Filter chips wrap onto new lines as they are added, so the height has to be
  // observed rather than recomputed on a fixed set of deps.
  useEffect(() => {
    const el = navRef.current;
    if (!el) return undefined;
    // Frozen while the brand row is folded: shrinking this reflows the page, and
    // the scroll anchoring that follows reads as an up-scroll to useHideOnScroll.
    const setHeight = () => {
      if (!brandRowVisible.current) return;
      document.documentElement.style.setProperty(
        '--nav-height',
        `${el.offsetHeight}px`
      );
    };
    setHeight();
    const observer = new ResizeObserver(setHeight);
    observer.observe(el);
    return () => observer.disconnect();
  }, [showBrandRow]);

  async function handleLogout() {
    await logout();
    dispatch(setLogout());
    dispatch(setShowLoginDialog(false));
    if (import.meta.env.VITE_POSTHOG_PROJECT_TOKEN) posthog.reset();
    navigate('/');
  }

  // The OTP email links here; signed out, the verify banner would never render.
  useEffect(() => {
    if (!isAuthenticated && searchParams.get('verify') === '1') {
      dispatch(setShowLoginDialog(true));
    }
  }, [isAuthenticated, searchParams, dispatch]);

  const ADMIN_DASHBOARD_URL = import.meta.env.VITE_ADMIN_DASHBOARD_URL;
  return (
    <div
      ref={navRef}
      role="banner"
      className="fixed inset-x-0 top-0 z-50 px-2 md:px-3"
    >
      <div className="rounded-b-2xl border border-t-0 bg-card bg-gradient-to-br from-green-500/35 via-emerald-300/10 to-green-600/30 shadow-[0_8px_30px_rgba(0,0,0,0.10)] supports-[backdrop-filter]:bg-card/60 supports-[backdrop-filter]:backdrop-blur-xl supports-[backdrop-filter]:backdrop-saturate-150">
        <ForgetPasswordDialog variant="outline" />
        <LoginDialog />
        <FeedSheet />

        <div
          data-testid="nav-brand-row"
          data-state={showBrandRow ? 'open' : 'folded'}
          className={`items-center justify-between gap-4 pb-2.5 pt-4 ${GUTTER} ${showBrandRow ? 'flex' : 'hidden'}`}
        >
          <div className="flex items-center gap-3 md:gap-4">
            <Link
              to="/"
              title="Filefinity"
              className="flex items-center gap-2.5"
            >
              <img
                src={filefinityMark}
                alt="Filefinity"
                className="h-8 w-auto object-contain md:h-9"
              />
              <span className="leading-tight">
                <span className="flex items-center gap-1.5">
                  <span className="text-lg font-bold text-heading md:text-xl">
                    Filefinity
                  </span>
                  <span className="rounded-full bg-blue-50 px-1.5 py-px text-[10px] font-bold uppercase tracking-wide text-blue-600">
                    Beta
                  </span>
                </span>
                <span className="block text-[11px] text-muted-foreground">
                  One site, infinite CNC files
                </span>
              </span>
            </Link>

            <div className="hidden h-9 w-px bg-border lg:block" />

            <a
              href="https://www.onefinitycnc.com/"
              target="_blank"
              rel="noopener noreferrer"
              title="Onefinity"
              className="hidden items-center gap-2.5 lg:flex"
            >
              <img
                src={onefinityMark}
                alt="Onefinity"
                className="h-8 w-auto object-contain"
              />
              <span className="leading-tight">
                <span className="block text-base font-bold tracking-wide text-heading">
                  ONEFINITY
                </span>
                <span className="block text-[11px] text-muted-foreground">
                  One Machine Infinite Possibilities
                </span>
              </span>
            </a>
          </div>

          <div className="hidden items-center gap-2 lg:flex">
            <RewardsProgramTermsModal
              trigger={
                <button
                  type="button"
                  className="flex h-11 shrink-0 items-center gap-3 rounded-full bg-gradient-to-b from-green-600 to-green-800 pl-1.5 pr-2.5 text-left text-white shadow-md transition-colors hover:from-green-700 hover:to-green-900"
                >
                  <span className="flex h-8 w-8 items-center justify-center rounded-full bg-white shadow-sm">
                    <Upload className="h-4 w-4 text-green-700" />
                  </span>
                  <span className="h-6 w-px bg-white/40" />
                  <span className="leading-tight">
                    <span className="block whitespace-nowrap text-[13px] font-extrabold uppercase tracking-wide">
                      Upload &amp; Earn
                    </span>
                    {/* Wrapped, the third line spills past the pill's fixed height */}
                    <span className="hidden whitespace-nowrap text-[10px] text-green-100 xl:block">
                      Earn Onefinity Store Credit
                    </span>
                  </span>
                  <RewardCoin coinType="gold" width={26} height={26} />
                </button>
              }
            />

            <button
              type="button"
              disabled
              title="Hub — coming soon"
              aria-label="Hub"
              className={`${TILE} relative cursor-default text-muted-foreground hover:border-border hover:bg-card hover:text-muted-foreground`}
            >
              <Users className="h-5 w-5" />
              <span className="hidden xl:inline">Hub</span>
              <span className="absolute -right-1.5 -top-1.5 z-10 rounded-full border border-amber-200 bg-amber-100 px-1.5 py-px text-[10px] font-bold uppercase tracking-wide text-amber-700">
                Soon
              </span>
            </button>

            <Link to="/blog" aria-label="Blog" className={TILE}>
              <FileText className="h-5 w-5" />
              <span className="hidden xl:inline">Blog</span>
            </Link>

            {isAuthenticated && <FeedTile className={TILE} />}

            {isAuthenticated ? (
              <>
                <div className="mx-1 h-9 w-px bg-border" />
                <Link
                  to="/project/v2/upload"
                  aria-label="Upload"
                  title="Upload"
                  className={TILE}
                >
                  <UploadCloud className="h-5 w-5 text-green-600" />
                  <span className="hidden 2xl:inline">Upload</span>
                </Link>

                <Link
                  to={`/${user?.username}`}
                  aria-label="My Files"
                  title="My Files"
                  className={TILE}
                >
                  <FolderOpen className="h-5 w-5" />
                  <span className="hidden 2xl:inline">My Files</span>
                </Link>

                <DropdownMenu>
                  <div className="relative ml-1">
                    <DropdownMenuTrigger asChild>
                      <ProfilePicture
                        src={user?.profilePicture}
                        alt={user?.firstname}
                        data-testid="user-menu"
                        points={user?.currentPoints}
                        tier={user?.tier}
                        variant="pill"
                      />
                    </DropdownMenuTrigger>
                  </div>
                  <DropdownMenuContent className="w-56 m-3">
                    <DropdownMenuLabel>My Account</DropdownMenuLabel>
                    <DropdownMenuSeparator />
                    <DropdownMenuGroup>
                      <DropdownMenuItem asChild>
                        <Link to={`/${user?.username}`}>Profile</Link>
                      </DropdownMenuItem>
                      <DropdownMenuItem asChild>
                        <Link to="/changePassword">Change Password</Link>
                      </DropdownMenuItem>
                      {user?.isAdmin && (
                        <DropdownMenuItem asChild>
                          <div className="flex items-center">
                            <a
                              href={ADMIN_DASHBOARD_URL}
                              target="_blank"
                              rel="noreferrer"
                            >
                              Admin Dashboard
                            </a>
                            <ArrowUpRight className="h-5 w-5" />
                          </div>
                        </DropdownMenuItem>
                      )}
                      <DropdownMenuItem onClick={handleLogout}>
                        Log out
                      </DropdownMenuItem>
                    </DropdownMenuGroup>
                  </DropdownMenuContent>
                </DropdownMenu>
              </>
            ) : (
              <>
                <div className="mx-1 h-9 w-px bg-border" />
                <LoginButton variant="outline" className="rounded-xl" />
                <Button variant="default" className="rounded-xl" asChild>
                  <Link to="/signup">Sign up</Link>
                </Button>
              </>
            )}
          </div>

          <MobileNav
            isAuthenticated={isAuthenticated}
            user={user}
            onLogout={handleLogout}
          />
        </div>

        {surface === 'feed' && (
          <div className={`pb-4 pt-3 ${GUTTER}`}>
            <div className="flex flex-wrap items-center gap-2">
              <FeedSearchInput className="w-full min-w-0 md:w-auto md:flex-1" />
              <FeedFilterControls />
            </div>
            <FeedFilterChips />
          </div>
        )}

        {!surface && (
          <div className={`pb-4 pt-3 ${GUTTER}`}>
            <FeedSearchInput className="w-full min-w-0" />
          </div>
        )}

        {surface === 'profile' && (
          <div className={`flex flex-col gap-2.5 pb-4 pt-3 ${GUTTER}`}>
            <ProfileSearchRow />
            <ProfileStatusTabs />
          </div>
        )}

        {isAuthenticated && !isVerified && (
          <p className="flex w-full items-center justify-center gap-2 border-t border-green-200 bg-green-50/80 px-4 py-2 text-sm font-medium text-green-900 last:rounded-b-2xl">
            <MessageSquareWarning className="h-4 w-4 shrink-0 text-green-700" />
            Verify your email to finish signup — we sent you a 6-digit
            verification code
            <VerifyAccountModal />
          </p>
        )}
      </div>
    </div>
  );
}

export default Navbar;
